import type { PairDashboardItem, PairDashboardPayload } from "./metricsApi";

const MAX_CACHED_RUN_PANELS = 40;
const MAX_CACHED_TREND_DASHBOARDS = 12;

const runPanelCache = new Map<string, PairDashboardPayload>();
const trendDashboardCache = new Map<string, PairDashboardPayload>();
let cacheNamespace = "anonymous";

export function setRunPanelCacheNamespace(uid: string | null | undefined): void {
  const next = uid?.trim() || "anonymous";
  if (next === cacheNamespace) return;
  cacheNamespace = next;
  runPanelCache.clear();
  trendDashboardCache.clear();
}

export function runPanelCacheKey(pairId: string, runId: string): string {
  return `${cacheNamespace}::${pairId}::${runId}`;
}

export function getCachedRunPanel(
  pairId: string,
  runId: string,
): PairDashboardPayload | null {
  return runPanelCache.get(runPanelCacheKey(pairId, runId)) ?? null;
}

export function deleteCachedRunPanel(pairId: string, runId: string): void {
  runPanelCache.delete(runPanelCacheKey(pairId, runId));
}

export function setCachedRunPanel(
  pairId: string,
  runId: string,
  payload: PairDashboardPayload,
): void {
  const key = runPanelCacheKey(pairId, runId);
  if (runPanelCache.has(key)) runPanelCache.delete(key);
  runPanelCache.set(key, payload);
  while (runPanelCache.size > MAX_CACHED_RUN_PANELS) {
    const oldest = runPanelCache.keys().next().value;
    if (oldest == null) break;
    runPanelCache.delete(oldest);
  }
}

export function clearRunPanelCache(): void {
  runPanelCache.clear();
}

export function getCachedTrendDashboard(datasetId: string): PairDashboardPayload | null {
  return trendDashboardCache.get(`${cacheNamespace}::${datasetId}`) ?? null;
}

export function setCachedTrendDashboard(
  datasetId: string,
  payload: PairDashboardPayload,
): void {
  const key = `${cacheNamespace}::${datasetId}`;
  if (trendDashboardCache.has(key)) trendDashboardCache.delete(key);
  trendDashboardCache.set(key, payload);
  while (trendDashboardCache.size > MAX_CACHED_TREND_DASHBOARDS) {
    const oldest = trendDashboardCache.keys().next().value;
    if (oldest == null) break;
    trendDashboardCache.delete(oldest);
  }
}

export function clearTrendDashboardCache(): void {
  trendDashboardCache.clear();
}

/** Build an immediate stub from the trend chart so the gauge/details update on click. */
export function buildOptimisticRunPanel(
  trend: PairDashboardPayload,
  agentId: string,
  runId: string,
): PairDashboardPayload {
  const series = trend.modelTrends.find((entry) => entry.agentId === agentId);
  const point = series?.points.find((entry) => entry.runId === runId);
  const metric = trend.correctnessMetric || "correctness";
  const aggregates: Record<string, number> = {};
  if (typeof point?.correctness === "number" && Number.isFinite(point.correctness)) {
    aggregates[metric] = point.correctness;
  }
  if (typeof point?.availability === "number" && Number.isFinite(point.availability)) {
    aggregates.success = point.availability;
  }

  return {
    agentId,
    datasetId: trend.datasetId,
    datasetName: trend.datasetName,
    agentName: series?.agentName ?? (trend.agentId === agentId ? trend.agentName : null),
    model: series?.model ?? (trend.agentId === agentId ? trend.model : null),
    connectorType: trend.connectorType,
    mode: trend.mode,
    runId,
    runCompletedAt: point?.createdAt ?? null,
    traceCount: 0,
    itemTotal: 0,
    costUsd: null,
    correctnessMetric: trend.correctnessMetric,
    aggregates,
    items: [],
    runHistory: [],
    experimentScores: [],
    modelTrends: trend.modelTrends,
  };
}

export function mergeRunPanelFromFetches(input: {
  base: PairDashboardPayload;
  items: PairDashboardItem[];
  itemTotal: number;
  costUsd: number | null;
  correctnessMetric: string | null;
}): PairDashboardPayload {
  const metric = input.correctnessMetric ?? input.base.correctnessMetric;
  const aggregates = { ...input.base.aggregates };
  if (input.items.length > 0 && metric) {
    const scores = input.items
      .map((item) => {
        const raw = item.scores[metric];
        if (typeof raw === "number" && Number.isFinite(raw)) return raw;
        const quality = Object.entries(item.scores).find(([name]) =>
          /correctness|judge-only/i.test(name),
        );
        if (typeof quality?.[1] === "number") return quality[1];
        if (item.evaluation && Number.isFinite(item.evaluation.score)) {
          return item.evaluation.score;
        }
        return null;
      })
      .filter((value): value is number => value != null);
    if (scores.length > 0) {
      aggregates[metric] = scores.reduce((sum, value) => sum + value, 0) / scores.length;
    }
  }

  return {
    ...input.base,
    items: input.items,
    itemTotal: input.itemTotal,
    traceCount: input.itemTotal,
    costUsd: input.costUsd,
    correctnessMetric: metric,
    aggregates,
  };
}
